import React, { useMemo, useEffect, useState, useCallback } from "react";
import { PropTypes } from "prop-types";
import { Box, Button, TextField, MenuItem } from "@mui/material";
import { useTheme } from "@mui/styles";
import { useDispatch } from "react-redux";
import { push } from "connected-react-router";
import { useTranslation } from "react-i18next";
import { intersectionBy, differenceBy, uniqBy, sortBy } from "lodash";
import { useLocations } from "../../hooks/locations";
import SearchBar from "../../components/SearchBar/SearchBar";
import AdminPageLayout from "../../components/FullPageLayout/AdminPageLayout";
import DualListContainer from "../../components/DualList/DualListContainer";
import Breadcrumb from "../../components/Breadcrumb/Breadcrumb";
import ButtonGroup from "../../components/Buttons/ButtonGroup";
import { buildGroupDetailInformationRoute } from "../../routes/AdminRoutes";
import {
  enqueueErrorSnackbar,
  enqueueSuccessSnackbar
} from "../../redux/actions/snackbarActions";
import { states } from "../../constants";
import useUser from "../../hooks/useUser";

const defaultFilterValues = {
  search: "",
  state: ""
};

const ManageGroupLocationsFilters = ({ onFilter, stateFilterOptions }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [filters, setFilters] = useState(defaultFilterValues);

  const handleSearchSubmit = newSearchValue => {
    setFilters({ ...filters, search: newSearchValue });
    onFilter({ search: newSearchValue, state: filters.state });
  };

  const handleClearFilters = () => {
    setFilters(defaultFilterValues);
    onFilter(defaultFilterValues);
  };

  const handleFilterChange = (filterKey, filterValue) => {
    setFilters({ ...filters, [filterKey]: filterValue });
  };

  return (
    <Box display="flex" alignItems="center" flexWrap="wrap" ml={-3} mb={-2}>
      <Box flex={10} minWidth={300} pl={3} pb={2}>
        <SearchBar
          onSearchSubmit={handleSearchSubmit}
          filled
          searchIconPosition="end"
          background={theme.palette.inputBackground}
          placeholder={t("manageGroupLocations.searchLabel")}
          value={filters.search}
          onSearchInput={value => handleFilterChange("search", value)}
          controlled
        />
      </Box>
      <Box
        flex={10}
        maxWidth={{ xs: "unset", sm: 300 }}
        minWidth={225}
        pl={3}
        pb={2}
      >
        <TextField
          select
          value={filters.state}
          onChange={ev => handleFilterChange("state", ev.target.value)}
          variant="filled"
          InputProps={{
            disableUnderline: true,
            "data-qa": "manage-group-locations-state-filter"
          }}
          fullWidth
          label={t("manageGroupLocations.stateLabel")}
        >
          <MenuItem value="">{t("filterLabels.showAll")}</MenuItem>
          {stateFilterOptions &&
            stateFilterOptions.map(({ value, label }) => (
              <MenuItem value={value} key={value}>
                {label}
              </MenuItem>
            ))}
        </TextField>
      </Box>
      <ButtonGroup pl={3} flex={1} pb={2}>
        <Button
          color="primary"
          variant="text"
          onClick={handleClearFilters}
          data-qa="manage-group-locations-clear-filters-btn"
        >
          {t("clearAllBtn")}
        </Button>
        <Button
          color="primary"
          variant="contained"
          onClick={() => onFilter({ ...filters })}
          data-qa="manage-group-locations-apply-filters-btn"
        >
          {t("filterList")}
        </Button>
      </ButtonGroup>
    </Box>
  );
};

const ManageGroupLocations = ({
  groupLocations,
  groupName,
  groupId,
  divisionId,
  onSave
}) => {
  const { t } = useTranslation();
  const [locations, setLocations] = useState([]);
  const { getLocations, isLoading: isLoadingLocations } = useLocations();
  const dispatch = useDispatch();
  const { isClient } = useUser();
  const [filters, setFilters] = useState({ search: "", state: "" });
  const [isUpdatingLocations, setIsUpdatingLocations] = useState(false);

  useEffect(() => {
    groupId &&
      getLocations(
        divisionId ? { filters: { divisionId: divisionId } } : {}
      ).then(locations => setLocations(locations.data));
  }, [groupId]);

  const sortLocations = locations =>
    sortBy(locations, [location => location.name.toLowerCase()]);

  const assignedLocations = useMemo(
    () => sortLocations(intersectionBy(locations, groupLocations, "id")),
    [locations, groupLocations]
  );
  const unassignedLocations = useMemo(
    () => sortLocations(differenceBy(locations, groupLocations, "id")),
    [locations, groupLocations]
  );

  const filterLocations = useCallback(
    locations =>
      sortLocations(
        locations.filter(
          location =>
            location.name
              ?.toLowerCase()
              .includes(filters.search.toLowerCase()) &&
            (!filters.state || filters.state === location.state)
        )
      ),
    [filters]
  );

  const getListItemDetails = item => {
    const addressLine1 = `${item.address1}${
      !!item.address2 ? `, ${item.address2}` : ""
    }`;
    const addressLine2 = `${item.city}, ${item.state} ${item.zip}`;
    const divisionLine = `${item.customer?.name}`;
    return {
      id: item.id,
      name: item.name,
      subtitles: [addressLine1, addressLine2, isClient && divisionLine]
    };
  };

  const getUpdatedGroupLocations = (initialList, newList) => {
    const updatedLocations = differenceBy(newList, initialList, "id");
    return updatedLocations.map(({ id: locationId }) => ({
      location: { type: "location", id: locationId },
      group: { type: "group", id: groupId }
    }));
  };

  const handleSave = (unselectedItems, selectedItems) => {
    const groupLocationsToCreate = getUpdatedGroupLocations(
      assignedLocations,
      selectedItems
    );
    const groupLocationsToDelete = getUpdatedGroupLocations(
      unassignedLocations,
      unselectedItems
    );
    const locationsWereUpdated =
      groupLocationsToCreate.length > 0 || groupLocationsToDelete.length > 0;
    if (locationsWereUpdated) {
      setIsUpdatingLocations(true);
      onSave(groupLocationsToCreate, groupLocationsToDelete).then(response => {
        setIsUpdatingLocations(false);

        if (response.errors) {
          dispatch(
            enqueueErrorSnackbar({
              message: t("manageGroupLocations.errorMsg")
            })
          );
        } else {
          dispatch(
            enqueueSuccessSnackbar({
              message: t(
                "administration.groupInformation.manageGroupSuccessMsg"
              ),
              options: {
                autoHideDuration: 10000
              }
            })
          );
        }
      });
    }
  };
  const isLoading = !groupId || isLoadingLocations;

  const stateFilterOptions = useMemo(() => {
    return uniqBy(locations, "state").map(location => ({
      value: location.state,
      label: states.find(({ value }) => location.state === value)
        ? states.find(({ value }) => location.state === value).label
        : ""
    }));
  }, [locations]);

  return (
    <AdminPageLayout
      title={
        <Breadcrumb
          routes={[
            t(`administration.groups`),
            groupName,
            t("manageGroupLocations.title")
          ]}
        />
      }
      onGoBackClick={() =>
        dispatch(push(buildGroupDetailInformationRoute(groupId)))
      }
    >
      <Box mt={3} flex={1} display="flex">
        <DualListContainer
          initialSelectedItems={assignedLocations}
          initialUnselectedItems={unassignedLocations}
          filters={
            <ManageGroupLocationsFilters
              onFilter={setFilters}
              stateFilterOptions={stateFilterOptions}
            />
          }
          filterUnselected={filterLocations}
          rowHeight={isClient ? 109 : 89}
          getListItemDetails={getListItemDetails}
          unselectedListHeader={t("manageGroupLocations.availableLocations")}
          selectedListHeader={t("manageGroupLocations.groupLocations")}
          onSave={handleSave}
          saveButtonText={t("manageGroupLocations.saveBtn")}
          isLoadingItems={isLoading}
          disabled={isLoading || isUpdatingLocations}
          isSaving={isUpdatingLocations}
        />
      </Box>
    </AdminPageLayout>
  );
};

export default ManageGroupLocations;

ManageGroupLocations.propTypes = {
  groupLocations: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string
    })
  ),
  groupName: PropTypes.string,
  groupId: PropTypes.string,
  onSave: PropTypes.func
};
